/**
 * TripMobileHeader — compact, touch-friendly trip header used at the top of
 * the trip detail (EditQuote) page.
 *
 *  - Payment status clearly at the top (Paid / Pending / Overdue badge).
 *  - One-tap stage pills: Enquiry → Quote → Booked → Ongoing → Completed.
 *    Friendly labels mapped over the real statuses (draft/sent/confirmed/
 *    completed) so LTV, reports & filters keep working. Tapping updates the
 *    status immediately — no need to open the full form.
 *  - Share button → bottom sheet: Share Preview / Share Voucher / Payment Link.
 *    All client links use the branded crm.travcations.com/{public_ref}/… shape
 *    (never internal DB ids).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Share2, Eye, Ticket, CreditCard, Copy, MessageCircle, Loader2 } from 'lucide-react';
import { tripPaymentStatus, PAYMENT_BADGE } from '@/utils/paymentStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { key: 'enquiry', label: 'Enquiry', status: 'draft' },
  { key: 'quote', label: 'Quote', status: 'sent' },
  { key: 'booked', label: 'Booked', status: 'confirmed' },
  { key: 'ongoing', label: 'Ongoing', status: 'confirmed' },
  { key: 'completed', label: 'Completed', status: 'completed' },
];

const parseDate = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d.getTime()) ? null : d; };

const currentStageKey = (quote) => {
  const st = (quote.status || 'draft').toLowerCase();
  if (st === 'completed') return 'completed';
  if (st === 'cancelled') return null;
  if (['confirmed', 'accepted'].includes(st)) {
    const start = parseDate(quote.travelStartDate);
    const end = parseDate(quote.travelEndDate) || start;
    const now = new Date();
    const startToday = new Date(); startToday.setHours(0, 0, 0, 0);
    if (start && end && start <= now && end >= startToday) return 'ongoing';
    return 'booked';
  }
  if (st === 'sent') return 'quote';
  return 'enquiry';
};

const TripMobileHeader = ({ quote, linkedItinerary, onStatusChange }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!quote?.tripId) return;
    axios.get(`${BACKEND_URL}/api/invoices?trip_id=${quote.tripId}`, { withCredentials: true })
      .then((r) => setInvoices(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => { /* no invoices is fine */ });
  }, [quote?.tripId]);

  const pay = tripPaymentStatus(quote, invoices);
  const activeKey = currentStageKey(quote);

  const setStage = async (stage) => {
    if (busy) return;
    if (stage.key === activeKey) return;
    setBusy(true);
    try {
      if (stage.status === 'completed') {
        const r = await axios.post(`${BACKEND_URL}/api/trips/${quote.id}/complete`, {}, { withCredentials: true });
        const rec = r.data?.reconciliation || {};
        toast.success(rec.status === 'balanced' ? 'Trip completed · balanced ✓' : 'Trip marked completed');
        onStatusChange?.('completed');
      } else {
        await axios.patch(`${BACKEND_URL}/api/quotes/${quote.id}/status`, { status: stage.status }, { withCredentials: true });
        toast.success(`Marked as ${stage.label}`);
        onStatusChange?.(stage.status);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update status');
    } finally {
      setBusy(false);
    }
  };

  // Branded client links (crm.travcations.com/{public_ref}/…).
  const itinRef = linkedItinerary?.public_ref || linkedItinerary?.id;
  let base = window.location.origin;
  try { if (linkedItinerary?.public_url) base = new URL(linkedItinerary.public_url).origin; } catch (_) { /* noop */ }
  const previewUrl = itinRef ? `${base}/${itinRef}/preview` : '';
  const voucherUrl = itinRef ? `${base}/${itinRef}/voucher` : '';

  const copy = async (url, label) => {
    if (!url) { toast.error('Create an itinerary first'); return; }
    try { await navigator.clipboard.writeText(url); toast.success(`${label} link copied`); }
    catch { window.prompt(`Copy ${label} link:`, url); }
  };
  const waShare = (url) => {
    if (!url) { toast.error('Create an itinerary first'); return; }
    const text = encodeURIComponent(`Hi ${quote.guestName || ''}, here are your ${quote.destination || 'trip'} details: ${url}`);
    const phone = (quote.phone || '').replace(/[^0-9]/g, '');
    window.open(phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid="trip-mobile-header">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-mono text-slate-400">{quote.displayTripId || quote.tripId}</p>
          <h2 className="text-lg font-bold text-slate-900 truncate">{quote.guestName || '(Unnamed)'}</h2>
          <p className="text-xs text-slate-500 truncate">{quote.destination || '—'}</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {pay ? (
            <span data-testid="trip-payment-status" className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${PAYMENT_BADGE[pay].cls}`}>
              {PAYMENT_BADGE[pay].label}
            </span>
          ) : (
            <span data-testid="trip-payment-status" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-slate-50 text-slate-500 border-slate-200">No invoice</span>
          )}
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            data-testid="trip-share-btn"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 min-h-[36px]"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
        </div>
      </div>

      {/* One-tap stage pills */}
      <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'none' }} data-testid="trip-stage-pills">
        {STAGES.map((s) => {
          const active = s.key === activeKey;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setStage(s)}
              disabled={busy}
              data-testid={`stage-pill-${s.key}`}
              className={`shrink-0 px-3 min-h-[40px] rounded-full text-xs font-semibold border transition-colors ${
                active ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Share bottom sheet */}
      <Sheet open={shareOpen} onOpenChange={setShareOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="trip-share-sheet">
          <SheetHeader>
            <SheetTitle>Share this trip</SheetTitle>
          </SheetHeader>
          <div className="mt-3 space-y-2 pb-4">
            <ShareRow icon={Eye} label="Share Preview" hint={previewUrl} onCopy={() => copy(previewUrl, 'Preview')} onWa={() => waShare(previewUrl)} testid="share-preview" />
            <ShareRow icon={Ticket} label="Share Voucher" hint={voucherUrl} onCopy={() => copy(voucherUrl, 'Voucher')} onWa={() => waShare(voucherUrl)} testid="share-voucher" />
            <button
              type="button"
              onClick={() => { setShareOpen(false); navigate(`/payment-links?tripId=${quote.tripId}`); }}
              data-testid="share-payment-link"
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 min-h-[56px] text-left"
            >
              <span className="w-9 h-9 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><CreditCard className="h-4 w-4" /></span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800">Payment Link</span>
                <span className="block text-xs text-slate-400 truncate">Create or copy a payment link</span>
              </span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const ShareRow = ({ icon: Icon, label, hint, onCopy, onWa, testid }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 min-h-[56px]" data-testid={`${testid}-row`}>
    <span className="w-9 h-9 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
    <div className="min-w-0 flex-1">
      <span className="block text-sm font-medium text-slate-800">{label}</span>
      <span className="block text-xs text-slate-400 truncate">{hint || 'Create an itinerary first'}</span>
    </div>
    <button type="button" onClick={onCopy} data-testid={`${testid}-copy`} aria-label={`Copy ${label}`} className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100">
      <Copy className="h-4 w-4" />
    </button>
    <button type="button" onClick={onWa} data-testid={`${testid}-wa`} aria-label={`WhatsApp ${label}`} className="w-10 h-10 rounded-lg flex items-center justify-center text-emerald-600 hover:bg-emerald-50">
      <MessageCircle className="h-4 w-4" />
    </button>
  </div>
);

export default TripMobileHeader;
